const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class ProjectService {
  getAuthToken() {
    return localStorage.getItem('token');
  }

  getHeaders() {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async getProjects(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/api/projects?${queryParams}`, {
        headers: this.getHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al obtener los proyectos');
      return data;
    } catch (error) {
      console.error('Error en getProjects:', error);
      throw error;
    }
  }

  async getAssignedUsers() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/assigned-users`, {
        headers: this.getHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al obtener usuarios asignados');
      return data;
    } catch (error) {
      console.error('Error en getAssignedUsers:', error);
      throw error;
    }
  }

  async getProjectById(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${id}`, {
        headers: this.getHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al obtener el proyecto');
      return data;
    } catch (error) {
      console.error('Error en getProjectById:', error);
      throw error;
    }
  }

  async createProjectFromProposal(proposalId, saleId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ proposalId, saleId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al crear proyecto desde propuesta');
      return data;
    } catch (error) {
      console.error('Error en createProjectFromProposal:', error);
      throw error;
    }
  }

  async createPhase(projectId, phaseData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/phases`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(phaseData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al crear fase');
      return data;
    } catch (error) {
      console.error('Error en createPhase:', error);
      throw error;
    }
  }

  async createProject(projectData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(projectData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al crear el proyecto');
      return data;
    } catch (error) {
      console.error('Error en createProject:', error);
      throw error;
    }
  }

  async updateProject(id, projectData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(projectData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al actualizar el proyecto');
      return data;
    } catch (error) {
      console.error('Error en updateProject:', error);
      throw error;
    }
  }

  async updatePhase(phaseId, phaseData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/phases/${phaseId}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify(phaseData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al actualizar la fase');
      return data;
    } catch (error) {
      console.error('Error en updatePhase:', error);
      throw error;
    }
  }

  async addPhaseObservation(phaseId, content) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/phases/${phaseId}/observations`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ content }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al agregar la observación');
      return data;
    } catch (error) {
      console.error('Error en addPhaseObservation:', error);
      throw error;
    }
  }

  async logPhaseWork(phaseId, workData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/phases/${phaseId}/work-logs`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(workData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al registrar trabajo');
      return data;
    } catch (error) {
      console.error('Error en logPhaseWork:', error);
      throw error;
    }
  }

  async deleteProject(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al eliminar el proyecto');
      return data;
    } catch (error) {
      console.error('Error en deleteProject:', error);
      throw error;
    }
  }

  async deletePhase(phaseId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/phases/${phaseId}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al eliminar fase');
      return data;
    } catch (error) {
      console.error('Error en deletePhase:', error);
      throw error;
    }
  }

  async getProjectActivities(projectId, phaseId = '') {
    try {
      let url = `${API_BASE_URL}/api/projects/${projectId}/activities`;
      if (phaseId) url += `?phaseId=${phaseId}`;
      const response = await fetch(url, {
        headers: this.getHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al obtener actividades');
      return data;
    } catch (error) {
      console.error('Error en getProjectActivities:', error);
      throw error;
    }
  }

  async generateTechnicalDocument(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/generate-technical-document`, {
        method: 'POST',
        headers: this.getHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al generar documento técnico');
      return data;
    } catch (error) {
      console.error('Error en generateTechnicalDocument:', error);
      throw error;
    }
  }

  async addSettlementPayment(projectId, paymentData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/settlement/payments`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(paymentData)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al registrar abono');
      return data;
    } catch (error) {
      console.error('Error en addSettlementPayment:', error);
      throw error;
    }
  }

  async updateSettlementConfig(projectId, config) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/settlement/config`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(config)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al actualizar liquidación');
      return data;
    } catch (error) {
      console.error('Error en updateSettlementConfig:', error);
      throw error;
    }
  }

  async triggerProjectCreation(saleId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/trigger-sale/${saleId}`, {
        method: 'POST',
        headers: this.getHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al generar proyecto');
      return data;
    } catch (error) {
      console.error('Error en triggerProjectCreation:', error);
      throw error;
    }
  }
}

const projectServiceInstance = new ProjectService();
export default projectServiceInstance;
